import OpenAI from 'openai';

let openai = null;

// Get environment variables with defaults
const getTemperature = () => {
  const envTemperature = process.env.REACT_APP_CHAT_TEMPERATURE;
  return envTemperature ? parseFloat(envTemperature) : 0.7;
};

// Initialize OpenAI client
export const initOpenAI = (apiKey) => {
  openai = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true // Note: In production, you should use a backend proxy
  });
  return openai;
};

// Get OpenAI client
export const getOpenAI = () => {
  if (!openai) {
    throw new Error('OpenAI client not initialized. Call initOpenAI first.');
  }
  return openai;
};

// Send a message to ChatGPT and get a response
export const sendMessageToGPT = async (messages) => {
  try {
    const response = await getOpenAI().chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: messages,
      temperature: getTemperature(),
      // No max_tokens - allow the model to generate as much text as needed
    });

    return response.choices[0].message.content;
  } catch (error) {
    console.error('Error calling OpenAI:', error);
    throw error;
  }
};

// Create a system message for the behavioral interviewer
export const createInterviewerSystemMessage = () => {
  return {
    role: "system",
    content: `You are a professional behavioral interviewer conducting a job interview. Your role is to assess the candidate's qualifications, experience, and fit for the position.

Guidelines:
1. Ask one question at a time and wait for the candidate's response.
2. Start with a brief introduction.
3. Focus on behavioral questions about specific experiences.
4. Examples:
   - "Tell me about a challenge at work. How did you handle it?"
   - "Describe a time you worked in a team to solve a problem."
   - "When did you adapt to a significant change?"
5. Ask follow-up questions when needed.
6. Tailor questions based on their resume/job description.
7. Be professional but friendly.
8. Ask 5-7 questions total.
9. When ending the interview, say "INTERVIEW COMPLETE" at the start of your final message.
10. In your final message, provide a letter grade (A+, A, B, C, D, or F) based on their overall performance.
11. Format your final evaluation as: "Your interview score: [GRADE]" at the end of your message.

Begin by introducing yourself briefly, then ask your first question.`
  };
};

// For backward compatibility
export const createInterviewerLegacyMessage = createInterviewerSystemMessage;
export const createInterviewProSystemMessage = createInterviewerSystemMessage;

// Create a system message for the science tutor
export const createTutorSystemMessage = () => {
  return {
    role: "system",
    content: `You are a professional science tutor specializing in physics, astronomy, mathematics, and related sciences. Your goal is to help students understand complex scientific concepts by explaining them clearly and engaging the student in a conversation.

Guidelines:
1. Be friendly, supportive, and encouraging.
2. Provide detailed explanations of scientific concepts when asked.
3. Use analogies and real-world examples to make abstract concepts more concrete.
4. Break down complex ideas into smaller, more manageable parts.
5. End your messages with a question or a leading statement to encourage further conversation.
6. If the student is confused, try explaining the concept in a different way.
7. Connect new information to concepts the student already understands.
8. Provide comprehensive answers to questions about astronomy and physics topics.
9. Include relevant equations, diagrams (described in text), and examples when appropriate.
10. Encourage critical thinking and scientific curiosity.

IMPORTANT FOR MATHEMATICAL FORMULAS:
- Write mathematical expressions without escaping special characters (no forward slashes before parentheses, brackets, etc.)
- Format equations as they would be read aloud, for example:
  * Write "F = ma" instead of "F = m\\a"
  * Write "E = mc²" instead of "E = mc\\²"
  * Write "∫f(x)dx" instead of "\\∫f(x)dx"
  * Write "[A] = concentration of A" instead of "\\[A\\] = concentration of A"
  * For fractions, write "a/b" instead of "\\frac{a}{b}"
- When explaining equations, use clear plain language that would be understandable when spoken.

Begin with the topic at hand.`
  };
}; 